// Free access, as a company's Subscription screen says it (migration 190,
// 16 October 2026). Sendinvo gives it from /admin; nothing in the app or on
// the web unlocks it, so there is nothing here to type, only what to say.
//
// The web's Subscription page reads this (src/js/screens/subscription.js).
// The iPhone says the same words in Swift (`FreeAccessCopy` in
// ios/Sendinvo/Billing/BillingSummary.swift), held to these by
// test/free-access-words.test.mjs reading the Swift. The two free access
// emails (server/mailer.js) say what comes next in these words too, worded
// for the company by the same test the screens make (`subscriptionCarriesOn`),
// so an email never says sending stops where the screen says it carries on.
//
// `freeAccess` is what `/billing/status` and the subscription block carry
// while it is live: `{ until }`, the last day ("2027-03-01") or null.
import { TEAM_COPY } from "./team.js";

const DAY_MS = 24 * 60 * 60 * 1000;

// How long before a last day the buttons come back, so somebody can
// subscribe before it ends: the month the ending email gives them
// (FREE_ACCESS_NOTICE_DAYS in server/free-access.js).
export const FREE_ACCESS_SUBSCRIBE_DAYS = 30;

export const FREE_ACCESS_COPY = Object.freeze({
  planLabel: "Plan",
  plan: "Sendinvo",
  statusLabel: "Status",
  status: "Free access",
  /** The account's Subscription row says it in a word. */
  rowValue: "Free",
  emailBefore: "We’ll email you a month before it ends.",
  appStore: "You’re still subscribed through the App Store, but you don’t need to be while you have free access.",
  /** Said by the note in the last month, and by both free access emails. */
  carriesOn: "your subscription carries on after that",
  /** What stops once there's no free access, trial or subscription left. */
  stops: "invoices and payment reminders won’t go out until you subscribe",
  /** And what doesn't, for a company whose subscription carries on. */
  keepsGoing: "invoices and payment reminders keep going out",
  /** A failed App Store payment: invoices wait for it, as the paywall says. Reminders don't stop for it (ADR-032). */
  waitsForPayment: "invoices won’t go out until your App Store payment goes through"
});

/**
 * Whether a company's subscription carries on once its free access ends: an
 * App Store one that renews (only the person can stop one, ADR-022), or a web
 * one that is active or trialing and not set to end (one free access set to
 * end is not). An App Store payment that failed isn't carrying on: once the
 * trial after free access ends the company is refused unless Apple's retry
 * collects (`appStorePaymentDue`). `subscription` is the row's or `/billing/status`'s
 * `{ provider, status, cancelAtPeriodEnd }`. The test the web's Subscription
 * page and the iPhone's `BillingSummary.freeAccess` make for the note's
 * `subscribed`, and what both free access emails are worded by.
 */
export function subscriptionCarriesOn(subscription) {
  if (!subscription) return false;
  const status = subscription.status || "none";
  const ending = subscription.cancelAtPeriodEnd === true;
  if (subscription.provider === "apple") return status === "active" && !ending;
  if (subscription.provider === "stripe") return (status === "active" || status === "trialing") && !ending;
  return false;
}

/** An App Store subscription whose payment failed, with renewal still on. */
export function appStorePaymentDue(subscription) {
  return subscription?.provider === "apple" && subscription.status === "past_due" && subscription.cancelAtPeriodEnd !== true;
}

// "your subscription carries on" -> "Your subscription carries on".
function sentence(words) {
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What the email a month before a last day says happens after it, for the
 * company it names. `trialDays` is the trial that follows free access.
 *
 *   "After that you have 30 days of everything, free, as any new account
 *    does. Then invoices and payment reminders won’t go out until you subscribe."
 *   "Your subscription carries on after that, so invoices and payment
 *    reminders keep going out."                              (`subscribed`)
 *   "After that you have 30 days of everything, free, as any new account
 *    does. Then invoices won’t go out until your App Store payment goes
 *    through."                                                (`paymentDue`)
 */
export function freeAccessEndingNext({ subscribed = false, paymentDue = false, trialDays } = {}) {
  if (subscribed) return `${sentence(FREE_ACCESS_COPY.carriesOn)}, so ${FREE_ACCESS_COPY.keepsGoing}.`;
  if (paymentDue) return `After that you have ${trialDays} days of everything, free, as any new account does. Then ${FREE_ACCESS_COPY.waitsForPayment}.`;
  return `After that you have ${trialDays} days of everything, free, as any new account does. Then ${FREE_ACCESS_COPY.stops}.`;
}

/**
 * What the email saying free access has ended (a Revoke) says comes next.
 * `until` is the trial's last day, written out ("29 November 2026"), or "".
 *
 *   "Everything stays unlocked until 29 November 2026. After that, invoices
 *    and payment reminders won’t go out until you subscribe."
 *   "Your subscription carries on, so invoices and payment reminders keep
 *    going out."                                              (`subscribed`)
 */
export function freeAccessEndedNext({ subscribed = false, paymentDue = false, until = "", trialDays } = {}) {
  if (subscribed) return `Your subscription carries on, so ${FREE_ACCESS_COPY.keepsGoing}.`;
  const stops = paymentDue ? FREE_ACCESS_COPY.waitsForPayment : FREE_ACCESS_COPY.stops;
  return until
    ? `Everything stays unlocked until ${until}. After that, ${stops}.`
    : `You have ${trialDays} days of everything, free, as any new account does. After that, ${stops}.`;
}

/** "1 March 2027", from a day, whatever the reader's time zone. */
export function freeAccessDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ""));
  if (!m) return "";
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC"
  });
}

/** "Free access", or "Free until 1 March 2027". */
export function freeAccessStatus(freeAccess) {
  const day = freeAccessDay(freeAccess?.until);
  return day ? `Free until ${day}` : FREE_ACCESS_COPY.status;
}

/**
 * Whether the last day is a month away or less, when Subscribe, Manage and
 * Restore come back. Never, with no last day.
 */
export function freeAccessEndsSoon(freeAccess, now = Date.now()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(freeAccess?.until || ""));
  if (!m) return false;
  const ends = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return ends - Number(now) <= FREE_ACCESS_SUBSCRIBE_DAYS * DAY_MS;
}

/**
 * The sentence under the plan. `companyName` is the company's own name;
 * without one, "your company".
 *
 *   "Sendinvo has given Shaw Electrical free access. Your whole team is included."
 *   + " We’ll email you a month before it ends."             (with a last day)
 *   + " It ends on 1 March 2027. Subscribe to keep sending invoices after that."
 *                                                             (in its last month)
 *   + " It ends on 1 March 2027, and your subscription carries on after that."
 *                                  (in its last month, with `subscribed`: a
 *                                   subscription that will renew past it)
 *   + " It ends on 1 March 2027. Update your App Store payment to keep sending
 *       invoices after that."       (in its last month, with `paymentDue`)
 */
export function freeAccessNote({ freeAccess, companyName = "", now = Date.now(), subscribed = false, paymentDue = false } = {}) {
  const name = String(companyName || "").trim() || "your company";
  const lead = `Sendinvo has given ${name} free access. ${TEAM_COPY.wholeTeamIncluded}`;
  const day = freeAccessDay(freeAccess?.until);
  if (!day) return lead;
  if (freeAccessEndsSoon(freeAccess, now)) {
    if (subscribed) return `${lead} It ends on ${day}, and ${FREE_ACCESS_COPY.carriesOn}.`;
    return paymentDue
      ? `${lead} It ends on ${day}. Update your App Store payment to keep sending invoices after that.`
      : `${lead} It ends on ${day}. Subscribe to keep sending invoices after that.`;
  }
  return `${lead} ${FREE_ACCESS_COPY.emailBefore}`;
}

/**
 * A web subscription free access set to end (server/free-access.js), said so
 * nobody wonders whether they're still paying.
 *   "Your web subscription ends on 5 November 2026, so you won’t be charged again."
 */
export function freeAccessWebEndingNote(periodEnd) {
  const when = periodEnd ? new Date(periodEnd) : null;
  if (!when || Number.isNaN(when.getTime())) return "Your web subscription has been cancelled, so you won’t be charged again.";
  const day = when.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" });
  return `Your web subscription ends on ${day}, so you won’t be charged again.`;
}
